import type { DynamicEntityRow } from '../api/strapiDynamicEntity';
import { airGroupFromGroupingFields, type SamplingPlanAirGroup } from './groupingAirSignal';

export type JenisRef = { documentId: string; label: string };

function asRecord(v: unknown): Record<string, unknown> | null {
  return v != null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function relDocId(rel: unknown): string {
  const rec = unwrapRel(rel);
  return String(rec?.documentId ?? '').trim();
}

function unwrapRel(v: unknown): Record<string, unknown> | null {
  if (v == null) return null;
  if (Array.isArray(v)) return unwrapRel(v[0]);
  const rec = asRecord(v);
  if (!rec) return null;
  if (rec.data != null && !('documentId' in rec)) return unwrapRel(rec.data);
  const attrs = asRecord(rec.attributes);
  return attrs ? { ...rec, ...attrs } : rec;
}

/** Jenis pengukuran id for sampling-form: bidang_pengujian, then quotation sample jenis. */
export function jenisPengukuranDocumentIdFromSamplingPlan(plan: unknown): string {
  const rec = unwrapRel(plan);
  if (!rec) return '';
  const fromBidang = relDocId(rec.bidang_pengujian);
  if (fromBidang) return fromBidang;
  const qsi = unwrapRel(rec.quotation_sample_information);
  return relDocId(qsi?.jenis_pengukuran);
}

/** Nama jenis dengan prioritas yang sama seperti id form sampling. */
export function jenisPengukuranNameFromSamplingPlan(plan: unknown): string {
  const rec = unwrapRel(plan);
  if (!rec) return '';
  const bidang = unwrapRel(rec.bidang_pengujian);
  if (String(bidang?.documentId ?? '').trim()) {
    return String(bidang?.nama_jenis ?? bidang?.nama ?? bidang?.name ?? '').trim();
  }
  const qsi = unwrapRel(rec.quotation_sample_information);
  const jenis = unwrapRel(qsi?.jenis_pengukuran);
  return String(jenis?.nama_jenis ?? jenis?.nama ?? jenis?.name ?? '').trim();
}

export function isAmbientAirQualityJenisName(name: string): boolean {
  return name.trim().toLowerCase() === 'ambient air quality';
}

export function jenisFromQsiRelation(qsi: unknown): JenisRef | null {
  const rec = asRecord(qsi);
  if (!rec) return null;
  const jenis = asRecord(rec.jenis_pengukuran);
  if (!jenis) return null;
  const documentId = String(jenis.documentId ?? '').trim();
  const label = String(jenis.nama_jenis ?? jenis.nama ?? jenis.name ?? '').trim();
  if (!documentId) return null;
  return { documentId, label };
}

export function buildJenisByQsiFromSampleLines(
  lines: Array<{
    documentId?: string | null;
    jenis_pengukuran?: { documentId?: string | null; nama_jenis?: string | null } | null;
  }>,
): Map<string, JenisRef> {
  const map = new Map<string, JenisRef>();
  for (const line of lines) {
    const qsiDocId = String(line.documentId ?? '').trim();
    const jenis = line.jenis_pengukuran;
    if (!qsiDocId || !jenis) continue;
    const documentId = String(jenis.documentId ?? '').trim();
    const label = String(jenis.nama_jenis ?? '').trim();
    if (documentId) map.set(qsiDocId, { documentId, label });
  }
  return map;
}

export function buildQsiUpdatedAtByDocIdFromSampleLines(
  lines: Array<{ documentId?: string | null; updatedAt?: string | null }>,
): Map<string, number> {
  const map = new Map<string, number>();
  for (const line of lines) {
    const qsiDocId = String(line.documentId ?? '').trim();
    if (!qsiDocId) continue;
    const ts = Date.parse(String(line.updatedAt ?? '')) || 0;
    if (ts) map.set(qsiDocId, ts);
  }
  return map;
}

export function buildJenisByQsiFromPlans(plans: DynamicEntityRow[]): Map<string, JenisRef> {
  const map = new Map<string, JenisRef>();
  for (const plan of plans) {
    const qsiDocId = relDocId(plan.quotation_sample_information);
    const jenis = jenisFromQsiRelation(plan.quotation_sample_information);
    if (qsiDocId && jenis) map.set(qsiDocId, jenis);
  }
  return map;
}

export function buildQsiUpdatedAtByDocIdFromPlans(plans: DynamicEntityRow[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const plan of plans) {
    const qsi = asRecord(plan.quotation_sample_information);
    const qsiDocId = relDocId(qsi);
    if (!qsiDocId || !qsi) continue;
    const ts = Date.parse(String(qsi.updatedAt ?? '')) || 0;
    if (ts) map.set(qsiDocId, ts);
  }
  return map;
}

/** Bidang plan sama dengan jenis pengukuran baris sampel lain pada WO yang sama. */
export function isCrossLineBidangContamination(
  plan: DynamicEntityRow,
  jenisByQsi: Map<string, JenisRef>,
): boolean {
  const qsiDocId = relDocId(plan.quotation_sample_information);
  const bidangDocId = relDocId(plan.bidang_pengujian);
  const own = qsiDocId ? jenisByQsi.get(qsiDocId) : undefined;
  if (!qsiDocId || !bidangDocId || !own || bidangDocId === own.documentId) return false;

  for (const [otherQsi, otherJenis] of jenisByQsi) {
    if (otherQsi !== qsiDocId && otherJenis.documentId === bidangDocId) return true;
  }
  return false;
}

/** Gunakan jenis quotation terbaru jika bidang plan stale (quotation lebih baru dari plan). */
export function shouldPreferQuotationJenisForBidang(
  plan: DynamicEntityRow,
  jenisByQsi: Map<string, JenisRef>,
  qsiUpdatedAtByQsi: Map<string, number> = new Map(),
): boolean {
  const qsiDocId = relDocId(plan.quotation_sample_information);
  const bidangDocId = relDocId(plan.bidang_pengujian);
  const own = qsiDocId ? jenisByQsi.get(qsiDocId) : undefined;
  if (!qsiDocId || !own || !bidangDocId || bidangDocId === own.documentId) return false;

  if (isCrossLineBidangContamination(plan, jenisByQsi)) return true;

  const qsiTs = qsiUpdatedAtByQsi.get(qsiDocId);
  if (!qsiTs) return false;
  const planTs = Date.parse(String(plan.updatedAt ?? '')) || 0;
  return qsiTs > planTs;
}

export function rawBidangLabelFromPlan(plan: DynamicEntityRow): string {
  const bidang = plan.bidang_pengujian;
  if (!bidang || typeof bidang !== 'object' || Array.isArray(bidang)) return '';
  const b = bidang as Record<string, unknown>;
  return String(b.nama_jenis ?? b.nama ?? b.name ?? '').trim();
}

export function effectiveBidangLabelFromSamplingPlan(
  plan: DynamicEntityRow,
  jenisByQsi: Map<string, JenisRef>,
  qsiUpdatedAtByQsi: Map<string, number> = new Map(),
): string {
  const qsiDocId = relDocId(plan.quotation_sample_information);
  if (shouldPreferQuotationJenisForBidang(plan, jenisByQsi, qsiUpdatedAtByQsi)) {
    const own = qsiDocId ? jenisByQsi.get(qsiDocId) : undefined;
    if (own?.label) return own.label;
  }
  return rawBidangLabelFromPlan(plan);
}

export function mergeJenisIndexes(...maps: Array<Map<string, JenisRef>>): Map<string, JenisRef> {
  const merged = new Map<string, JenisRef>();
  for (const map of maps) {
    for (const [key, value] of map) merged.set(key, value);
  }
  return merged;
}

export function mergeUpdatedAtIndexes(...maps: Array<Map<string, number>>): Map<string, number> {
  const merged = new Map<string, number>();
  for (const map of maps) {
    for (const [key, value] of map) merged.set(key, value);
  }
  return merged;
}

export type { SamplingPlanAirGroup } from './groupingAirSignal';

/** Same resolution as berita-acara PDF / backend `airGroupFromSamplingPlan` (v1 perencanaan_sampling). */
export function airGroupFromSamplingPlan(plan: unknown): SamplingPlanAirGroup {
  const rec = unwrapRel(plan);
  if (!rec) return 'non_air';
  const qsi = unwrapRel(rec.quotation_sample_information);
  const jenis = unwrapRel(qsi?.jenis_pengukuran) ?? unwrapRel(rec.bidang_pengujian);
  const bidang = unwrapRel(rec.bidang_pengujian) ?? jenis;
  const grouping = unwrapRel(jenis?.grouping) ?? unwrapRel(bidang?.grouping);
  return airGroupFromGroupingFields(grouping) ?? 'non_air';
}
